/**
 * The colors a school may give a grade.
 *
 * A grade's color is read at a glance by a parent on their dashboard, so this
 * is a fixed set rather than a free color picker: every value here clears 4.5:1
 * against the light background these are shown on, and the set holds together
 * as a palette. A color wheel offers a school a thousand ways to pick something
 * illegible, and no way to pick something that matches the others.
 *
 * Ordered as a spectrum — settled greens and blues through to the warm end —
 * so a school laying out A through F is choosing along one axis rather than
 * hunting a grid.
 *
 * Yellow is the one compromise. These are rendered as text on a white ground,
 * where a bright yellow is simply unreadable, so this is the yellowest value
 * that still reads: it clears 3:1 rather than the 4.5:1 the rest of the set
 * holds. A true lemon yellow would need the grade drawn as a filled chip with
 * dark text on it rather than as colored text.
 */

export interface GradeColor {
  name: string;
  hex: string;
}

export const GRADE_COLORS: readonly GradeColor[] = [
  { name: 'Forest', hex: '#2f6f4e' },
  { name: 'Teal', hex: '#1f6b68' },
  { name: 'Navy', hex: '#2a3d5c' },
  { name: 'Steel', hex: '#566b8f' },
  { name: 'Slate', hex: '#475569' },
  { name: 'Plum', hex: '#6b3a6b' },
  { name: 'Yellow', hex: '#b8860b' },
  { name: 'Amber', hex: '#9a6114' },
  { name: 'Rust', hex: '#9c4a1f' },
  { name: 'Crimson', hex: '#8f2d2d' },
];

/**
 * Last resort only. The server fills any band that has no color of its own
 * from the default scale (Academics::GradeScale.default_color_for), so a real
 * grade should never reach the UI colorless; this covers a row being built in
 * the editor before it has been saved.
 */
export const FALLBACK_GRADE_COLOR = '#475569';

export function gradeColorName(hex: string | null | undefined): string {
  if (!hex) return 'Slate';
  const match = GRADE_COLORS.find((color) => color.hex.toLowerCase() === hex.toLowerCase());
  // A school that set a color before this list existed keeps it; it just has no name.
  return match?.name ?? 'Custom';
}
